import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useGlobalFilters } from '../../hooks/useGlobalFilters';
import { histogramChartStore } from './HistogramChartStore';
import { getGroupTitleById } from '../../utils/groupTitles';
import DataWindowSlider from '../../components/DataWindowSliderPV';
import Pagination from '../../components/Pagination';
import { formatRuDate } from '../../utils/ruDateUtils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import './TimeSeries.scss';
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor('interval_start', {
    header: 'Интервал',
    cell: (info) => {
      const start = formatRuDate(info.row.original.interval_start) || '-';
      const end = formatRuDate(info.row.original.interval_end) || '-';
      return `${start} - ${end}`;
    },
  }),
  columnHelper.accessor('white_count', { header: 'Белые дни' }),
  columnHelper.accessor('black_count', { header: 'Черные дни' }),
  columnHelper.accessor('white_percentage', {
    header: 'Белые (%)',
    cell: (info) => info.row.original.total_records === 0 ? 'Нет данных' : `${info.getValue()}%`,
  }),
  columnHelper.accessor('black_percentage', {
    header: 'Черные (%)',
    cell: (info) => info.row.original.total_records === 0 ? 'Нет данных' : `${info.getValue()}%`,
  }),
  columnHelper.accessor('alarm_level', { 
    header: 'Уровень напряженности',
    cell: (info) => {
      if (info.row.original.total_records === 0) return 'Нет данных';
      const blackPercentage = info.row.original.black_percentage;
      if (blackPercentage > 50) {
        return 'Повышен уровень напряж-ти (агрессии)';
      } else if (blackPercentage > 30) {
        return 'Умеренный уровень';
      } else {
        return 'Норма';
      }
    }
  }),
  columnHelper.accessor('total_records', { header: 'Дней с данными' }),
  columnHelper.accessor('no_data_count', { header: 'Нет данных (дней)' }),
];

function formatNumber(val) {
  if (typeof val === "number") {
    return val.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }
  if (!isNaN(Number(val))) {
    return Number(val).toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }
  return val ?? "";
}

function getGroupNamesByIds(ids) {
  if (!Array.isArray(ids)) return [];
  return ids.map(getGroupTitleById);
}

const HistogramChart = observer(() => {
  const globalFilters = useGlobalFilters();
  const store = histogramChartStore;
  const [inputInterval, setInputInterval] = React.useState(store.interval);
  const [dataWindow, setDataWindow] = React.useState({ start: 0, end: 100 });

  // Обновляем локальное состояние при изменении стора
  React.useEffect(() => {
    setInputInterval(store.interval);
  }, [store.interval]);

  // Автоматически обновляем фильтры и подгружаем данные при изменении глобальных фильтров
  React.useEffect(() => {
    store.setFilters({
      startDate: globalFilters.startDate,
      endDate: globalFilters.endDate,
      selectedGroupIds: globalFilters.selectedGroups,
    });
    store.fetchChartData();
    // eslint-disable-next-line
  }, [globalFilters.filtersString]);

  // Debounce для интервала - отдельный useEffect
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputInterval !== store.interval) {
        store.setInterval(inputInterval);
        store.fetchChartData();
      }
    }, 500); // Задержка 500мс для ввода интервала
    
    return () => clearTimeout(timeoutId);
  }, [inputInterval, store]);

  // Сброс окна данных при обновлении набора
  React.useEffect(() => {
    setDataWindow({ start: 0, end: 100 });
  }, [store.chartData.length]);

  // Обработчик прокрутки окна данных
  const updateWindow = (value) => {
    const newStart = Number(value);
    setDataWindow({ start: newStart, end: newStart + 100 });
  };

  // Подготовка видимого подмножества данных
  const allData = store.chartData || [];
  const visibleData = useMemo(() => {
    if (allData.length === 0) return [];
    const startIndex = Math.min(dataWindow.start, allData.length - 1);
    const endIndex = Math.min(dataWindow.end, allData.length);
    return allData.slice(startIndex, endIndex);
  }, [allData, dataWindow]);

  // Текст диапазона дат для слайдера
  const dateRangeText = useMemo(() => {
    if (visibleData.length === 0) return 'Нет данных';
    const first = visibleData[0];
    const last = visibleData[visibleData.length - 1];
    return `Показаны данные: ${formatRuDate(first.interval_start) || '-'} - ${formatRuDate(last.interval_end) || '-'}`;
  }, [visibleData]);

  // График и таблица используют один набор данных. Сортировка и пагинация
  // таблицы выполняются локально и не создают дополнительные HTTP-запросы.
  const table = useReactTable({
    data: store.chartData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 },
      sorting: [{ id: 'interval_start', desc: false }],
    },
  });

  const chartDataConfig = useMemo(
    () => ({
      labels: visibleData.map((record) => {
        const start = formatRuDate(record.interval_start) || '-';
        const end = formatRuDate(record.interval_end) || '-';
        return (record.interval_start == record.interval_end) ? start : `${start} - ${end}`;
      }),
      datasets: [
        {
          label: 'Белые дни',
          data: visibleData.map((record) => record.total_records > 0 ? record.white_percentage : null),
          backgroundColor: '#ffffff',
          borderColor: '#000000',
          borderWidth: 1,
        },
        {
          label: 'Черные дни',
          data: visibleData.map((record) => record.total_records > 0 ? record.black_percentage : null),
          backgroundColor: '#2d3748',
          borderColor: '#000000',
          borderWidth: 1,
        },
        {
          label: 'Нет данных',
          data: visibleData.map((record) => record.total_records === 0 ? 100 : null),
          backgroundColor: '#cbd5e1',
          borderColor: '#64748b',
          borderWidth: 1,
        },
      ],
    }),
    [visibleData]
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#2d3748',
          font: { size: 14, family: "'Inter', sans-serif" },
        },
      },
      tooltip: {
        backgroundColor: '#2d3748',
        titleColor: '#fff',
        bodyColor: '#fff',
        callbacks: {
          label: (tooltipItem) => {
            const record = visibleData[tooltipItem.dataIndex];
            if (record.total_records === 0) return `Нет данных (дней: ${record.no_data_count})`;
            return `${tooltipItem.dataset.label}: ${tooltipItem.raw}% (Дней: ${
              record[tooltipItem.dataset.label === 'Белые дни' ? 'white_count' : 'black_count']
            })`;
          },
          footer: (items) => {
            const record = visibleData[items[0]?.dataIndex];
            return record?.total_records > 0 && record.no_data_count > 0
              ? `Нет данных (дней): ${record.no_data_count}` : '';
          },
        },
      },
    },
    scales: {
      x: {
        // title: {
        //   display: true,
        //   text: 'Интервал дат',
        //   color: '#2d3748',
        //   font: { size: 14, family: "'Inter', sans-serif", weight: '600' },
        // },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 50,
          maxRotation: 45,
          minRotation: 45,
          color: '#4a5568',
          font: { size: 12, family: "'Inter', sans-serif" },
        },
      },
      y: {
        title: {
          display: true,
          text: 'Процент (%)',
          color: '#2d3748',
          font: { size: 14, family: "'Inter', sans-serif", weight: '600' },
        },
        beginAtZero: true,
        max: 100,
        ticks: {
          color: '#4a5568',
          font: { size: 12, family: "'Inter', sans-serif" },
        },
      },
    },
    devicePixelRatio: 2,
  };

  // Экспорт в Excel
  const handleExportExcel = async () => {
    const filters = globalFilters.filtersString ? JSON.parse(globalFilters.filtersString) : {};
    const startDate = filters.startDate || "";
    const endDate = filters.endDate || "";
    const groupNames = getGroupNamesByIds(filters.selectedGroups || []);
    const graphTitle = "Временные_ряды";
    const fileName = `${graphTitle}_${groupNames.join("_")}_${startDate}_${endDate}.xlsx`;
    const allData = store.chartData || [];
    const ws_data = [
      ["Date", "white_count", "black_count", "white_percentage", "black_percentage", "alarm_level", "total_records", "no_data_count"],
      ...allData.map(d => {
        const blackPercentage = d.black_percentage;
        let alarmLevel = d.total_records === 0 ? 'Нет данных' : 'Норма';
        if (blackPercentage > 50) {
          alarmLevel = 'Повышен уровень напряж-ти (агрессии)';
        } else if (blackPercentage > 30) {
          alarmLevel = 'Умеренный уровень';
        }
        return [
          `${formatRuDate(d.interval_start) || '-'} - ${formatRuDate(d.interval_end) || '-'}`,
          formatNumber(d.white_count),
          formatNumber(d.black_count),
          d.total_records === 0 ? 'Нет данных' : formatNumber(d.white_percentage),
          d.total_records === 0 ? 'Нет данных' : formatNumber(d.black_percentage),
          alarmLevel,
          formatNumber(d.total_records),
          formatNumber(d.no_data_count)
        ];
      })
    ];
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Данные");
    XLSX.writeFile(wb, fileName);
  };
  // Экспорт в PDF
  const handleExportPDF = async () => {
    const filters = globalFilters.filtersString ? JSON.parse(globalFilters.filtersString) : {};
    const startDate = filters.startDate || "";
    const endDate = filters.endDate || "";
    const groupNames = getGroupNamesByIds(filters.selectedGroups || []);
    const graphTitle = "Временные_ряды";
    const fileName = `${graphTitle}_${groupNames.join("_")}_${startDate}_${endDate}.pdf`;
    const allData = store.chartData || [];
    const input = document.querySelector("#histogram-chart-container");
    if (!input) { alert("График не найден для экспорта!"); return; }
    const canvas = await html2canvas(input);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");
    pdf.addImage(imgData, "PNG", 10, 10, 270, 80);
    autoTable(pdf, {
      startY: 100,
      head: [["Date", "white_count", "black_count", "white_percentage", "black_percentage", "alarm_level", "total_records", "no_data_count"]],
      body: allData.map(d => {
        const blackPercentage = d.black_percentage;
        let alarmLevel = d.total_records === 0 ? 'Нет данных' : 'Норма';
        if (blackPercentage > 50) {
          alarmLevel = 'Повышен уровень напряж-ти (агрессии)';
        } else if (blackPercentage > 30) {
          alarmLevel = 'Умеренный уровень';
        }
        return [
          `${formatRuDate(d.interval_start) || '-'} - ${formatRuDate(d.interval_end) || '-'}`,
          formatNumber(d.white_count),
          formatNumber(d.black_count),
          d.total_records === 0 ? 'Нет данных' : formatNumber(d.white_percentage),
          d.total_records === 0 ? 'Нет данных' : formatNumber(d.black_percentage),
          alarmLevel,
          formatNumber(d.total_records),
          formatNumber(d.no_data_count)
        ];
      }),
      styles: { font: "helvetica", fontSize: 10 }
    });
    pdf.save(fileName);
  };

  // Проверка выбранных групп
  const hasSelectedGroups = globalFilters.selectedGroups && globalFilters.selectedGroups.length > 0;

  return (
    <div className="time-series">
      <div className="info-block" style={{
          background: '#e3f2fd',
          borderRadius: '12px',
          padding: '20px 28px',
          marginBottom: '12px',
          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.07)'
        }}>
          <h3 style={{ color: '#1976d2', marginTop: 0, marginBottom: 12, fontWeight: 600 }}>
            Справка по временным рядам
          </h3>
          <ul style={{ margin: 0, paddingLeft: 22, color: '#333', fontSize: '1.05em' }}>
          <p>
          <b>Белые</b> и <b>чёрные</b> столбцы показывают долю дней в интервале.
          Сначала рассчитывается одно среднее значение коэффициента за день по всем сообщениям
          выбранных сообществ. Белый день отклоняется от среднего за выбранный период не более чем
          на одно стандартное отклонение, чёрный — более чем на одно. Дни без рассчитанного
          коэффициента исключаются из среднего, стандартного отклонения и долей белых/чёрных дней.
          Полностью пустые интервалы отмечены серым как «Нет данных»; высота серого столбца
          служит обозначением пропуска. Число пропущенных дней указано в таблице и подсказках.</p>

          </ul>

        </div>




        <div style={{           marginBottom: 10, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between'  }}>

          
          <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
            <div>
              <label style={{ fontWeight: 500, marginRight: 8 }}>Период усреднения:</label>



              <select
                value={inputInterval}
                onChange={e => setInputInterval(Number(e.target.value))}
                style={{ borderRadius: 6, border: '1px solid #ccc', padding: 4 }}
              >
                <option value={1}>День</option>
                <option value={7}>Неделя</option>
                <option value={30}>Месяц</option>
              </select>



            </div>
          </div>
          <div style={{ display: 'flex', gap: 12}}>
            <button className="time-series-export-btn" onClick={handleExportExcel}>Экспорт в Excel</button>
            <button className="time-series-export-btn" onClick={handleExportPDF}>Экспорт в PDF</button>
          </div>
        </div>


      <div className="time-series-container">
        {/* Интервал и кнопки экспорта */}




        {/* График */}
        <div className="time-series-content-column">
          <div className="time-series-content-box" style={{ boxShadow: 'none', borderRadius: 0, padding: 0 }}>
            <h2 style={{ marginBottom: 16 }}>Анализ данных</h2>
            <div id="histogram-chart-container" className="time-series-chart" style={{ flexGrow: 1, width: '100%', minHeight: 400, background: 'none', boxShadow: 'none', borderRadius: 0, padding: 0, margin: 0 }}>
              {!hasSelectedGroups ? (
                <div className="time-series-no-data">Нет выбранной группы</div>
              ) : store.chartLoading ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  padding: '40px',
                  flexDirection: 'column',
                  gap: '12px',
                  minHeight: '400px'
                }}>
                  <div className="time-series-table-spinner"></div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Загрузка данных графика...</div>
                </div>
              ) : store.chartError ? (
                <div className="time-series-no-data">Ошибка: {store.chartError.message}</div>
              ) : store.chartData.length > 0 ? (
                <div className="time-series-chart-wrapper" style={{ width: '100%', minHeight: 400, background: 'none', boxShadow: 'none', borderRadius: 0, padding: 0, margin: 0 }}>
                  {store.chartData.length > 100 && (
                    <div style={{ margin: '0 0 12px 0', display: 'flex', justifyContent: 'center' }}>
                      <DataWindowSlider
                        aggregation={'day'}
                        dataWindow={dataWindow}
                        maxDataLength={store.chartData.length}
                        updateWindow={updateWindow}
                        dateRangeText={dateRangeText}
                      />
                    </div>
                  )}
                  <div style={{ width: '100%', flexGrow: 1, minHeight: 450 }}>
                    <Bar data={chartDataConfig} options={chartOptions} />
                  </div>
                </div>
              ) : (
                <div className="time-series-no-data">Нет данных для отображения</div>
              )}
            </div>
            {/* Таблица */}
            <div className="time-series-table">
              {store.chartLoading ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  padding: '40px',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div className="time-series-table-spinner"></div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Загрузка данных таблицы...</div>
                </div>
              ) : store.chartError ? (
                <div className="time-series-no-data">Ошибка: {store.chartError.message}</div>
              ) : (
                <table className="time-series-modern-table">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            onClick={header.column.getToggleSortingHandler()}
                            className={header.column.getIsSorted() ? 'sorted' : ''}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.getIsSorted() ? (header.column.getIsSorted() === 'asc' ? ' ↑' : ' ↓') : ''}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {store.chartData.length > 0 && (
                <div style={{ marginTop: 8, textAlign: 'right', color: '#6b7280', fontSize: 12 }}>
                  Обработано интервалов: {store.chartData.length}
                </div>
              )}
              {store.chartData.length > 0 && (
                <Pagination
                  currentPage={table.getState().pagination.pageIndex + 1}
                  totalPages={table.getPageCount()}
                  onPageChange={(page) => {
                    table.setPageIndex(page - 1);
                  }}
                  maxVisiblePages={5}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default HistogramChart;
